/**
 * Promo Gift Popup — Alchemy v4
 *
 * Correcciones:
 * · Protección contra doble registro del custom element
 * · "Mostrar solo una vez por sesión" usa cookie sin expiración
 *   (persiste entre páginas, se borra al cerrar el navegador)
 * · "Mostrar solo una vez por dispositivo" usa localStorage
 */

class PromoGiftPopup extends HTMLElement {
  constructor() {
    super();
    this._shown = false;
    this._pollInterval = null;
    this._lastCartToken = null;
  }

  connectedCallback() {
    this._setupClose();

    // Modo testing: mostrar siempre, sin condiciones ni límites
    if (this.dataset.mode === 'testing') {
      setTimeout(() => this._show(), 600);
      return;
    }

    // ── Verificar límites ANTES de hacer cualquier cosa ──────────────────
    if (this._sessionShown())  return;  // ya se mostró en esta sesión
    if (this._deviceShown())   return;  // ya se mostró en este dispositivo

    // ── Disparar según modo ───────────────────────────────────────────────
    if (this.dataset.triggerMode === 'time') {
      const delay = (parseFloat(this.dataset.delay) || 30) * 1000;
      setTimeout(() => this._show(), delay);

    } else {
      // cart_contains
      this._interceptFetch();
      this._startPolling();
    }
  }

  disconnectedCallback() {
    if (this._pollInterval) clearInterval(this._pollInterval);
  }

  // ─── Cookie de sesión (se borra al cerrar el navegador) ──────────────────
  _sessionKey()   { return 'pgp_sess_' + this.dataset.popupId; }
  _deviceKey()    { return 'pgp_dev_'  + this.dataset.popupId; }

  _sessionShown() {
    if (this.dataset.sessionOnce !== 'true') return false;
    // Cookie sin max-age ni expires = cookie de sesión del navegador
    return document.cookie.split(';').some(c => c.trim().startsWith(this._sessionKey() + '=1'));
  }

  _markSession() {
    if (this.dataset.sessionOnce !== 'true') return;
    // Sin path ni dominio para que aplique solo al sitio actual
    document.cookie = this._sessionKey() + '=1; path=/; SameSite=Lax';
  }

  _deviceShown() {
    if (this.dataset.showOnce !== 'true') return false;
    return localStorage.getItem(this._deviceKey()) === '1';
  }

  _markDevice() {
    if (this.dataset.showOnce !== 'true') return;
    localStorage.setItem(this._deviceKey(), '1');
  }

  // ─── Interceptar fetch y XHR ─────────────────────────────────────────────
  _interceptFetch() {
    if (window.__pgpFetchPatched) return; // evitar doble parche si hay 2 instancias
    window.__pgpFetchPatched = true;
    const self = this;

    const originalFetch = window.fetch;
    window.fetch = function(...args) {
      const url = typeof args[0] === 'string' ? args[0] : (args[0]?.url || '');
      return originalFetch.apply(this, args).then(res => {
        if (url.includes('/cart/add')) setTimeout(() => self._fetchCartAndCheck(), 700);
        return res;
      });
    };

    const origOpen = XMLHttpRequest.prototype.open;
    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function(m, url, ...r) {
      this._pgpUrl = url;
      return origOpen.apply(this, [m, url, ...r]);
    };
    XMLHttpRequest.prototype.send = function(...a) {
      if (this._pgpUrl?.includes('/cart/add')) {
        this.addEventListener('load', () => setTimeout(() => self._fetchCartAndCheck(), 700));
      }
      return origSend.apply(this, a);
    };
  }

  // ─── Polling ──────────────────────────────────────────────────────────────
  _startPolling() {
    this._fetchCartAndCheck(); // revisión inmediata
    this._pollInterval = setInterval(() => {
      if (this._shown) { clearInterval(this._pollInterval); return; }
      this._fetchCartAndCheck();
    }, 2500);
  }

  _fetchCartAndCheck() {
    if (this._shown) return;
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const token = cart.token + '_' + cart.item_count;
        if (token === this._lastCartToken) return;
        this._lastCartToken = token;
        this._checkConditions(cart);
      })
      .catch(() => {});
  }

  // ─── Lógica del carrito ───────────────────────────────────────────────────
  _checkConditions(cart) {
    if (this._shown || !cart?.items) return;

    const triggerIds = (this.dataset.triggerProductIds || '')
      .split(',').map(s => s.trim()).filter(Boolean);
    const minQty = parseInt(this.dataset.minQuantity, 10) || 1;

    const eligible = triggerIds.length === 0
      ? cart.item_count
      : cart.items.reduce((sum, item) =>
          triggerIds.includes(String(item.product_id)) ? sum + item.quantity : sum, 0);

    if (eligible >= minQty) setTimeout(() => this._show(), 500);
  }

  // ─── Mostrar / cerrar ─────────────────────────────────────────────────────
  _show() {
    if (this._shown) return;
    if (this.dataset.mode !== 'testing') {
      if (this._sessionShown() || this._deviceShown()) return;
    }

    this._shown = true;
    this._markSession();
    this._markDevice();

    this.classList.add('is-visible');
    document.body.style.overflow = 'hidden';
    const focusable = this.querySelectorAll('button, a[href]');
    if (focusable.length) focusable[0].focus();
  }

  _close() {
    this.classList.remove('is-visible');
    document.body.style.overflow = '';
  }

  _setupClose() {
    this.querySelectorAll('[data-close]').forEach(el =>
      el.addEventListener('click', () => this._close())
    );
    const backdrop = this.querySelector('[data-backdrop]');
    if (backdrop) backdrop.addEventListener('click', () => this._close());
    document.addEventListener('keydown', e => {
      if (e.key === 'Escape' && this.classList.contains('is-visible')) this._close();
    });
  }
}

// Protección contra doble definición del custom element
if (!customElements.get('promo-gift-popup')) {
  customElements.define('promo-gift-popup', PromoGiftPopup);
}